// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * When each plan is the plan.
 *
 * A set of logistics drawings without dates is a pile of alternates: nothing
 * on the sheets says which is current, which came first, or when the laydown
 * moves off the north lot. Give each one a stretch of time and the same pile
 * becomes a sequence — Demolition, Mar 3–28; Excavation, Mar 31–May 9 — which
 * is the thing a superintendent presents and an owner can act on.
 *
 * Dates are optional and unenforced. A plan with no dates is a plan nobody has
 * dated yet, not an error, and nothing here reorders the set: the order is the
 * one the user dragged the tabs into, because the drawing sequence and the
 * date sequence are not always the same thing and the software should not have
 * an opinion about which is wrong.
 */

export interface Phase {
  /** ISO "YYYY-MM-DD". */
  start?: string;
  end?: string;
}

export interface DatedPlan { id: string; name: string; phase?: Phase }

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** A date string we can work with, or undefined. */
export function cleanDay(v: unknown): string | undefined {
  const s = String(v ?? "").trim();
  if (!ISO.test(s)) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return undefined;
  // Round-tripped through a date so 2026-02-31 is rejected rather than
  // silently becoming March.
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return undefined;
  return s;
}

/** The phase on a plan, with anything unusable dropped. */
export function phaseOf(plan: DatedPlan | null | undefined): Phase {
  return { start: cleanDay(plan?.phase?.start), end: cleanDay(plan?.phase?.end) };
}

export function isDated(p: Phase | null | undefined): boolean {
  return !!(p && (p.start || p.end));
}

/**
 * Parsed as a local day.
 *
 * `new Date("2026-03-03")` is parsed as UTC midnight, which in every American
 * timezone prints as the 2nd. A logistics plan dated a day early is worse than
 * one with no dates at all.
 */
export function parseDay(iso: string | undefined): Date | null {
  const ok = cleanDay(iso);
  if (!ok) return null;
  const [y, m, d] = ok.split("-").map(Number);
  return new Date(y, m - 1, d);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mar 3" — the year is left off, since it is carried by the range. */
export function formatDay(iso: string | undefined, withYear = false): string {
  const d = parseDay(iso);
  if (!d) return "";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}${withYear ? `, ${d.getFullYear()}` : ""}`;
}

/**
 * The range as it reads on a tab or a sheet.
 *
 * A half-filled range still says something useful — "from Mar 3" is a real
 * statement about a plan whose end is not settled — so it is shown rather than
 * withheld until both ends exist.
 */
export function phaseRange(p: Phase | null | undefined, withYear = false): string {
  const s = cleanDay(p?.start), e = cleanDay(p?.end);
  if (s && e) {
    const a = parseDay(s)!, b = parseDay(e)!;
    // Across a year boundary both ends carry their own year; within one year
    // it is said once at the end, or "Mar 3-28" leaves out which March.
    if (a.getFullYear() !== b.getFullYear()) {
      return `${formatDay(s, withYear)}–${formatDay(e, withYear)}`;
    }
    const right = a.getMonth() === b.getMonth() ? String(b.getDate()) : formatDay(e);
    return `${formatDay(s)}–${right}${withYear ? `, ${a.getFullYear()}` : ""}`;
  }
  if (s) return `from ${formatDay(s, withYear)}`;
  if (e) return `until ${formatDay(e, withYear)}`;
  return "";
}

/** How many days the phase covers, both ends counted. */
export function phaseDays(p: Phase | null | undefined): number | null {
  const a = parseDay(p?.start), b = parseDay(p?.end);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
}

/** The duration as people say it: weeks once there are enough of them. */
export function phaseLength(p: Phase | null | undefined): string {
  const days = phaseDays(p);
  if (days === null || days <= 0) return "";
  if (days < 14) return `${days} day${days === 1 ? "" : "s"}`;
  const weeks = Math.round(days / 7);
  return `${weeks} week${weeks === 1 ? "" : "s"}`;
}

/** What is wrong with these dates, if anything. */
export function phaseIssue(p: Phase | null | undefined): string | null {
  const a = parseDay(p?.start), b = parseDay(p?.end);
  if (a && b && b.getTime() < a.getTime()) return "ends before it starts";
  return null;
}

/** The whole sequence's span, for a cover sheet or a summary line. */
export function sequenceSpan(plans: DatedPlan[]): Phase | null {
  const starts = plans.map((p) => phaseOf(p).start).filter(Boolean) as string[];
  const ends = plans.map((p) => phaseOf(p).end).filter(Boolean) as string[];
  if (!starts.length && !ends.length) return null;
  return {
    start: starts.sort()[0],
    end: ends.sort()[ends.length - 1],
  };
}

const DAY = 86400000;

function toIso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * The dates a schedule phase actually falls on.
 *
 * The schedule stores weeks from the project start, which is the right thing
 * for a gantt and useless on a drawing: nobody posts "week 4" on a trailer
 * wall. This converts, so a logistics plan can borrow a bar's dates instead of
 * having them typed in again and drift from it.
 *
 * The end is the last day of the phase rather than the first of the next, so
 * a one-week phase reads Mar 3–9, not Mar 3–10.
 */
export function phaseFromSchedule(
  projectStartDate: string, startWeek: number, durationWeeks: number,
): Phase | null {
  const base = parseDay(cleanDay(projectStartDate));
  if (!base) return null;
  const sw = Number.isFinite(startWeek) ? Math.max(0, startWeek) : 0;
  const dw = Number.isFinite(durationWeeks) ? Math.max(0, durationWeeks) : 0;
  const start = new Date(base.getTime() + sw * 7 * DAY);
  const end = new Date(start.getTime() + Math.max(0, dw * 7 - 1) * DAY);
  return { start: toIso(start), end: toIso(end) };
}

/**
 * A schedule phase's span in weeks, segments and all.
 *
 * A phase on the gantt can be broken into segments -- excavation here, then
 * again after the foundations -- and its own startWeek and durationWeeks are
 * kept only for compatibility. Reading those directly gives the first segment
 * and quietly loses the rest, so the span is taken from the segments when
 * there are any.
 */
export function weeksSpan(p: {
  startWeek?: number; durationWeeks?: number;
  segments?: Array<{ startWeek: number; durationWeeks: number }>;
}): { startWeek: number; durationWeeks: number } {
  const segs = (p.segments || []).filter((s) =>
    Number.isFinite(s?.startWeek) && Number.isFinite(s?.durationWeeks));
  if (!segs.length) {
    return {
      startWeek: Number.isFinite(p.startWeek) ? Math.max(0, p.startWeek!) : 0,
      durationWeeks: Number.isFinite(p.durationWeeks) ? Math.max(0, p.durationWeeks!) : 0,
    };
  }
  const from = Math.min(...segs.map((s) => s.startWeek));
  const to = Math.max(...segs.map((s) => s.startWeek + s.durationWeeks));
  return { startWeek: Math.max(0, from), durationWeeks: Math.max(0, to - from) };
}

/**
 * The sequence as a line of text, for a cover note or a summary.
 *
 * Built here rather than in the view because the same sentence belongs on the
 * exported sheet, in the panel and eventually in a transmittal, and three
 * copies of it would be three slightly different sentences.
 */
export function sequenceSummary(plans: DatedPlan[]): string {
  const dated = plans.filter((p) => isDated(phaseOf(p)));
  if (!dated.length) return "";
  const span = sequenceSpan(plans);
  const spanText = span ? phaseRange(span, true) : "";
  return `${dated.length} of ${plans.length} plan${plans.length === 1 ? "" : "s"} dated`
    + (spanText ? ` · ${spanText}` : "");
}
